import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { LoginService } from '../login/login.service';
import { Router } from '@angular/router';
import { CprInfo } from 'projects/shared/src/public-api';
import { environment } from 'projects/shared/src/environments/environment';
import { ApiService } from '../api-service/api.service';
@Injectable({
  providedIn: 'root',
})
export class CprService {
  constructor(
    private router: Router,
    private loginService: LoginService,
    private apiService: ApiService
  ) {}

  /**
   * Retrieves CPR logs for the specified date range from the server.
   *
   * @param {string} startDate - The start date for the log retrieval (format: YYYY-MM-DD).
   * @param {string} endDate - The end date for the log retrieval (format: YYYY-MM-DD).
   * @returns {Observable<any>} - An Observable that emits the CPR logs
   * or an error if the request fails.
   */
  getCPRLogs(startDate: string, endDate: string): Observable<any> {
    const accessToken = this.loginService.getActiveToken();
    const refreshToken = this.loginService.getRefreshToken();

    if (accessToken && refreshToken) {
      const isExpired = this.loginService.isTokenExpired(accessToken);

      let headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      });

      if (isExpired) {
        /**
         * Refresh the token and retry the API call.
         */
        return this.loginService.refreshAccessToken(refreshToken).pipe(
          switchMap((newAccessToken) => {
            headers = headers.set('Authorization', `Bearer ${newAccessToken}`);

            return this.apiService.makeApiCall<any>(
              `${environment.apiGetCPRLogs}?startDate=${startDate}&endDate=${endDate}`,
              headers,
              'GET'
            );
          }),
          catchError((error) => {
            console.error('Error refreshing token', error);
            this.loginService.clearTokens();
            this.router.navigate(['/login']);
            return throwError(() => error);
          })
        );
      }

      // Proceed with the API call
      return this.apiService.makeApiCall<any>(
        `${environment.apiGetCPRLogs}?startDate=${startDate}&endDate=${endDate}`,
        headers,
        'GET'
      );
    } else {
      /**
       * If tokens are unavailable, redirect to the login page.
       */
      console.error('Error with token in getCPRLogs');
      this.loginService.clearTokens();
      this.router.navigate(['/login']);
      return of(null);
    }
  }

  /**
   * Looks up CPR information using the given CPR details.
   *
   * @param {CprInfo} cprInfo - The CPR information to be sent to the server for lookup.
   * @returns {Observable<any>} - An Observable that emits the CPR lookup result
   * or an error if the request fails.
   */
  lookupCPR(cprInfo: CprInfo): Observable<any> {
    const accessToken = this.loginService.getActiveToken();
    const refreshToken = this.loginService.getRefreshToken();

    if (accessToken && refreshToken) {
      const isExpired = this.loginService.isTokenExpired(accessToken);

      let headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      });

      if (isExpired) {
        /**
         * Refresh the token and retry the API call.
         */
        return this.loginService.refreshAccessToken(refreshToken).pipe(
          switchMap((newAccessToken) => {
            headers = headers.set('Authorization', `Bearer ${newAccessToken}`);
            return this.apiService.makeApiCall<any>(
              environment.apiGetInfoFromCPR,
              headers,
              'POST',
              cprInfo
            );
          }),
          catchError((error) => {
            console.error('Error refreshing token', error);
            this.loginService.clearTokens();
            this.router.navigate(['/login']);
            return throwError(() => error);
          })
        );
      }

      // Proceed with the API call
      return this.apiService.makeApiCall<any>(
        environment.apiGetInfoFromCPR,
        headers,
        'POST',
        cprInfo
      );
    } else {
      /**
       * If tokens are unavailable, redirect to the login page.
       */
      console.error('Error with token in lookupCPR');
      this.loginService.clearTokens();
      this.router.navigate(['/login']);
      return of(null);
    }
  }
}
